import { toast } from '@/composables/baseToast'
import { supabase } from '@/composables/supabaseClient'
import executeSandboxedCode from '@/helpers/executeSandboxedCode'
import toastThrowError from '@/helpers/toastThrowError'
import { tryCatchAsync } from '@/helpers/tryCatch'
import uuidv7 from '@/helpers/uuidv7'
import type { AssemblyVariables, AssemblyVariableValue } from '@/types/customTypes'
import type { Tables } from '@/types/database.types'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useDatabaseStore } from './databaseStore'

/**Maintains estimate state in memory to prevent unnecessary data fetching */
export const useEstimateStore = defineStore('estimate', () => {
  const databaseStore = useDatabaseStore()
  const estimate = ref<Tables<'estimate'>>()
  const estimateItems = ref<Tables<'estimate_item'>[]>()
  const estimateAssemblies = ref<Tables<'estimate_assembly'>[]>()
  const estimateSortTypes = ref<Tables<'estimate_sort_type'>[]>()
  const estimateSortTypeOptions = ref<Tables<'estimate_sort_type_option'>[]>()
  const estimateSortTypeOptionItems = ref<Tables<'estimate_sort_type_option_item'>[]>()

  async function loadEstimate(estimateId: string) {
    if (estimate.value && estimate.value.id === estimateId) {
      // The current estimate is already in memory. Prevent refetching data.
      return
    }
    const result = await supabase
      .from('estimate')
      .select(
        `*,
        estimate_item(*),
        estimate_assembly(*),
        estimate_sort_type(*)
        `,
      )
      .eq('id', estimateId)
      .single()
    if (result.error) throw toastThrowError('Unable to load estimate', result.error)
    const estimateSortTypeOptionsResult = await supabase
      .from('estimate_sort_type_option')
      .select('*')
      .in(
        'estimate_sort_type_id',
        result.data.estimate_sort_type.map((est) => est.id),
      )
    if (estimateSortTypeOptionsResult.error) throw toastThrowError('Error loading sort types')
    estimateItems.value = result.data.estimate_item
    estimateAssemblies.value = result.data.estimate_assembly
    estimateSortTypes.value = result.data.estimate_sort_type
    estimateSortTypeOptions.value = estimateSortTypeOptionsResult.data
    const estimateSortTypeOptionItemsResult = await supabase
      .from('estimate_sort_type_option_item')
      .select('*')
      .in(
        'estimate_sort_type_option_id',
        estimateSortTypeOptionsResult.data.map((esto) => esto.id),
      )
    if (estimateSortTypeOptionItemsResult.error)
      throw toastThrowError('Unable to load estimate sort type option items')
    estimateSortTypeOptionItems.value = estimateSortTypeOptionItemsResult.data

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { estimate_item: ei, ...estimateOnly } = result.data
    estimate.value = estimateOnly
  }

  type ItemsAssemblies = {
    unique_id: number
    code: string
    name: string
    uom_code: string
    uom_id: string
    type: 'Item' | 'LookupItem' | 'Assembly'
    assembly_id?: string
    items: Tables<'item'>[]
  }
  /**Combined items and assemblies for use when adding items to an estimate. */
  const itemsAndAssemblies = computed<ItemsAssemblies[]>(() => {
    const items = databaseStore.items ?? []
    const assemblies = databaseStore.assemblies ?? []
    const itemsAssemblies: ItemsAssemblies[] = []
    let uniqueId = 1
    items.forEach((i) => {
      const subdivision = databaseStore.itemDivisions?.find((d) => d.id == i.item_division_id)
      const division = databaseStore.itemDivisions?.find(
        (d) => d.id == subdivision?.parent_division_id,
      )
      itemsAssemblies.push({
        unique_id: uniqueId,
        code: `${division?.code}.${subdivision?.code}.${i.code}`,
        name: i.name,
        uom_code: databaseStore.unitsOfMeasure?.find((uom) => uom.id == i.uom_id)?.code ?? '',
        uom_id: i.uom_id,
        type: 'Item',
        items: [i],
      })
      uniqueId += 1
    })
    assemblies.forEach((a) => {
      const division = databaseStore.assembyDivisions?.find((d) => d.id == a.assembly_division_id)
      const assemblyItems = databaseStore.assemblyItems?.filter((ai) => ai.assembly_id === a.id)
      const items = databaseStore.items?.filter((i) =>
        assemblyItems?.some((ai) => ai.item_id === i.id),
      )
      itemsAssemblies.push({
        code: `${division?.code}.${a.code}`,
        name: a.name,
        uom_code:
          databaseStore.unitsOfMeasure?.find((uom) => uom.id === a.unit_of_measure_id)?.code ?? '',
        uom_id: a.unit_of_measure_id,
        type: 'Assembly',
        items: items ?? [],
        unique_id: uniqueId,
        assembly_id: a.id,
      })
      uniqueId += 1
    })
    itemsAssemblies.sort((a, b) => a.code.localeCompare(b.code))
    return itemsAssemblies
  })

  // NOTE If this computation is slow when recalculating rows, it can be offloaded to a worker
  /**Provides common calculations for estimate items */
  function computeEstimateItem(estimateItem: Tables<'estimate_item'>) {
    if (!estimate.value) throw new Error('Estimate not yet loaded')
    const currentEstimate = estimate.value
    const item = databaseStore.items?.find((i) => i.id === estimateItem.item_id)
    const subdivision = databaseStore.itemDivisions?.find((d) => d.id === item?.item_division_id)
    const division = databaseStore.itemDivisions?.find(
      (d) => d.id === subdivision?.parent_division_id,
    )
    const materialDirectCost =
      (estimateItem.material_cost ?? 0) *
      (1 + (currentEstimate.material_cost_markup ?? 0)) *
      (estimateItem.quantity ?? 0)
    const laborDirectCost =
      (estimateItem.labor_cost ?? 0) *
      (estimateItem.labor_productivity_per_hr ?? 1) *
      (1 + (currentEstimate.labor_cost_markup ?? 0)) *
      (estimateItem.quantity ?? 0)
    const subcontractorDirectCost =
      (estimateItem.subcontractor_cost ?? 0) *
      (1 + (currentEstimate.subcontractor_cost_markup ?? 0)) *
      (estimateItem.quantity ?? 0)
    const equipmentDirectCost =
      (estimateItem.equipment_cost ?? 0) *
      (estimateItem.equipment_productivity_per_hr ?? 1) *
      (1 + (currentEstimate.equipment_cost_markup ?? 0)) *
      (estimateItem.quantity ?? 0)
    const otherDirectCost =
      (estimateItem.other_cost ?? 0) *
      (1 + (currentEstimate.other_cost_markup ?? 0)) *
      (estimateItem.quantity ?? 0)
    const materialPrice = materialDirectCost * (1 + (currentEstimate.material_ohp_markup ?? 0))
    const laborPrice = laborDirectCost * (1 + (currentEstimate.labor_ohp_markup ?? 0))
    const subcontractorPrice =
      subcontractorDirectCost * (1 + (currentEstimate.subcontractor_ohp_markup ?? 0))
    const equipmentPrice = equipmentDirectCost * (1 + (currentEstimate.equipment_ohp_markup ?? 0))
    const otherPrice = otherDirectCost * (1 + (currentEstimate.other_ohp_markup ?? 0))
    const totalCost =
      materialDirectCost +
      laborDirectCost +
      subcontractorDirectCost +
      equipmentDirectCost +
      otherDirectCost
    const totalPrice = materialPrice + laborPrice + subcontractorPrice + equipmentPrice + otherPrice
    const unitCost = totalCost / (estimateItem.quantity ?? 1)
    const unitPrice = totalPrice / (estimateItem.quantity ?? 1)
    const laborHours = (estimateItem.labor_productivity_per_hr ?? 0) * (estimateItem.quantity ?? 0)
    const equipmentHours =
      (estimateItem.equipment_productivity_per_hr ?? 0) * (estimateItem.quantity ?? 0)
    return {
      code: `${division?.code}.${subdivision?.code}.${item?.code}`,
      estimate_item_id: estimateItem.id,
      material_direct_cost: materialDirectCost,
      labor_direct_cost: laborDirectCost,
      subcontractor_direct_cost: subcontractorDirectCost,
      equipment_direct_cost: equipmentDirectCost,
      other_direct_cost: otherDirectCost,
      material_price: materialPrice,
      labor_price: laborPrice,
      subcontractor_price: subcontractorPrice,
      equipment_price: equipmentPrice,
      other_price: otherPrice,
      total_cost: totalCost,
      total_price: totalPrice,
      unit_cost: unitCost,
      unit_price: unitPrice,
      labor_hours: laborHours,
      equipment_hours: equipmentHours,
    }
  }

  async function calculateItemQtyFromAssemblyFormula(
    formula: string,
    variables?: Record<string, AssemblyVariableValue>,
  ) {
    let result: number | undefined = undefined
    if (variables) {
      Object.values(variables).forEach((value) => {
        if (value === '') throw toastThrowError('All variables must have a value')
      })
      const { data, error } = await tryCatchAsync(
        async () => await executeSandboxedCode(formula, variables),
      )
      if (error) {
        if (error.message.includes('is not defined')) {
          const missingVariableName = error.message.split(' is not defined')[0]
          toast(`Variable "${missingVariableName}" is not defined`, { type: 'error' })
          return
        } else {
          throw error
        }
      }
      result = data as number | undefined
    }
    const roundedResult = result ? Math.round(result * 100) / 100 : undefined
    return roundedResult
  }

  /**Returns item costs with default costs applied if necessary */
  function getItemCosts(itemId: string): Tables<'item_cost'> | undefined {
    if (!estimate.value) {
      throw Error(
        'No estimate is loaded. Use loadEstimate function to load an estimate before working with estimate data.',
      )
    }
    let itemCost = databaseStore.itemCosts?.find(
      (i) => i.item_id === itemId && i.cost_source_id === estimate.value!.cost_source_id,
    )
    const defaultCostSource = databaseStore.costSources?.find((cs) => cs.name === 'Default')
    if (defaultCostSource && defaultCostSource.id !== itemCost?.cost_source_id) {
      // Item costs are not coming from default cost source
      const defaultItemCost = databaseStore.itemCosts?.find(
        (i) => i.item_id === itemId && i.cost_source_id === defaultCostSource.id,
      )
      if (!itemCost) {
        itemCost = defaultItemCost
      } else {
        // Check all costs for empty values and replace with defaults if they exist
        if (!itemCost.material_cost) {
          itemCost.material_cost = defaultItemCost?.material_cost ?? null
        }
        if (!itemCost.labor_cost) {
          itemCost.labor_cost = defaultItemCost?.labor_cost ?? null
        }
        if (!itemCost.subcontractor_cost) {
          itemCost.subcontractor_cost = defaultItemCost?.subcontractor_cost ?? null
        }
        if (!itemCost.equipment_cost) {
          itemCost.equipment_cost = defaultItemCost?.subcontractor_cost ?? null
        }
        if (!itemCost.other_cost) {
          itemCost.other_cost = defaultItemCost?.other_cost ?? null
        }
      }
    }
    return itemCost
  }

  async function createEstimateItemsByAssembly(
    assemblyId: string,
    variables: Record<string, AssemblyVariableValue>,
  ) {
    if (!estimate.value) {
      throw Error(
        'No estimate is loaded. Use loadEstimate function to load an estimate before working with estimate data.',
      )
    }
    const assemblyDB = databaseStore.assemblies?.find((a) => a.id === assemblyId)
    if (!assemblyDB) throw Error('Unable to locate assembly in memory. Reload and try again')

    const assemblyVariables = assemblyDB.variables as AssemblyVariables
    Object.keys(assemblyVariables ?? {}).forEach((assemblyVariableName) => {
      if (!variables[assemblyVariableName]) {
        throw Error(`Missing assembly variable ${assemblyVariableName}`)
      }
    })
    const assemblyResult = await supabase
      .from('estimate_assembly')
      .insert({
        id: uuidv7(),
        assembly_id: assemblyId,
        estimate_id: estimate.value.id,
        name: assemblyDB.name,
        uom_id: assemblyDB.unit_of_measure_id,
        variables: assemblyDB.variables,
        variable_values: variables,
      })
      .select()
      .single()
    if (assemblyResult.error) throw assemblyResult.error
    const assemblyItems =
      databaseStore.assemblyItems?.filter((ai) => ai.assembly_id === assemblyId) ?? []
    const newEstimateItems = await Promise.all(
      assemblyItems.map(async (assemblyItem) => {
        // TODO - if the item_id is undefined, the item should be dynamically looked up based on it's attributes
        const itemCosts = getItemCosts(assemblyItem.item_id)
        const item = databaseStore.items?.find((i) => i.id === assemblyItem.item_id)
        if (!item) throw Error('Unable to get item from memory.')
        const newEstimateItem: Omit<Tables<'estimate_item'>, 'tenant_id'> = {
          id: uuidv7(),
          item_id: assemblyItem.item_id,
          estimate_id: estimate.value!.id,
          uom_id: item.uom_id,
          accounting_code: item.accounting_code,
          name: item.name,
          quantity: assemblyItem.formula
            ? ((await calculateItemQtyFromAssemblyFormula(assemblyItem.formula, variables)) ?? null)
            : null,
          material_cost: itemCosts?.material_cost ?? null,
          labor_cost: itemCosts?.labor_cost ?? null,
          subcontractor_cost: itemCosts?.subcontractor_cost ?? null,
          equipment_cost: itemCosts?.equipment_cost ?? null,
          other_cost: itemCosts?.other_cost ?? null,
          labor_type_id: item.labor_type_id,
          labor_productivity_per_hr: item.labor_productivity_per_hr,
          equipment_type_id: item.equipment_type_id,
          equipment_productivity_per_hr: item.equipment_productivity_per_hr,
          note: null,
          estimate_assembly_id: assemblyResult.data.id ?? null,
          formula: assemblyItem.formula,
        }
        return newEstimateItem
      }),
    )
    const estimateItemResult = await supabase
      .from('estimate_item')
      .insert(newEstimateItems)
      .select()
    if (estimateItemResult.error) throw Error('Unable to add items')
    estimateItems.value = estimateItems.value?.concat(estimateItemResult.data)
    estimateAssemblies.value?.push(assemblyResult.data)
  }

  async function createEstimateItem(itemId: string, qty: number | null) {
    if (!estimate.value) {
      throw Error(
        'No estimate is loaded. Use loadEstimate function to load an estimate before working with estimate data.',
      )
    }
    const itemDB = databaseStore.items?.find((i) => i.id === itemId)
    if (!itemDB) throw Error('Unable to locate item in memory.')
    const itemCost = getItemCosts(itemId)
    const newEstimateItem: Omit<Tables<'estimate_item'>, 'tenant_id' | 'estimate_assembly_id'> = {
      id: uuidv7(),
      item_id: itemDB.id,
      estimate_id: estimate.value.id,
      uom_id: itemDB.uom_id,
      accounting_code: itemDB.accounting_code,
      name: itemDB.name,
      quantity: qty,
      material_cost: itemCost?.material_cost ?? null,
      labor_cost: itemCost?.labor_cost ?? null,
      subcontractor_cost: itemCost?.subcontractor_cost ?? null,
      equipment_cost: itemCost?.equipment_cost ?? null,
      other_cost: itemCost?.other_cost ?? null,
      labor_type_id: itemDB.labor_type_id ?? null,
      labor_productivity_per_hr: itemDB.labor_productivity_per_hr,
      equipment_type_id: itemDB.equipment_type_id,
      equipment_productivity_per_hr: itemDB.equipment_productivity_per_hr,
      note: null,
      formula: null,
    }
    const result = await supabase.from('estimate_item').insert(newEstimateItem).select().single()
    if (result.error) throw result.error
    estimateItems.value?.push(result.data)
  }

  return {
    estimate,
    estimateItems,
    itemsAndAssemblies,
    estimateAssemblies,
    estimateSortTypes,
    estimateSortTypeOptions,
    estimateSortTypeOptionItems,
    loadEstimate,
    computeEstimateItem,
    calculateItemQtyFromAssemblyFormula,
    createEstimateItemsByAssembly,
    createEstimateItem,
  }
})
